import React from 'react';
import { RocketLaunchIcon } from '@heroicons/react/24/outline';
import { HChapter, HMetaphor, HSection, HParagraph, HStep, HTip, HBold, HWarning } from '../HandbookContent';

export default function ChapterPublishing() {
  return (
    <HChapter
      title="Publishing & Versioning"
      subtitle="How to take a playbook from draft to live use, and how versions work."
    >
      <HMetaphor
        icon={<RocketLaunchIcon className="w-5 h-5 text-lex-accent" />}
        title="Going Live"
      >
        Building a playbook is like drafting a brief — you write, review, and revise before filing.
        Publishing is the moment you file it with the court. Once published, it's the official version
        that governs how matters move through your workflow. If you need to make changes later, you
        create a new version — just like filing an amended brief.
      </HMetaphor>

      <HSection title="The Playbook Lifecycle">
        <HParagraph>
          Every playbook moves through a series of stages, from creation to retirement:
        </HParagraph>
        <div className="space-y-2 mb-4">
          {[
            { label: 'Draft', color: 'bg-gray-400', desc: 'Being built or edited. Not yet available for use with matters.' },
            { label: 'Active', color: 'bg-emerald-500', desc: 'Published and in use. Matters can be governed by this playbook.' },
            { label: 'Superseded', color: 'bg-amber-500', desc: 'Replaced by a newer version. Existing matters continue, but new ones use the latest version.' },
            { label: 'Inactive', color: 'bg-gray-500', desc: 'Temporarily disabled. No new matters can use it, but it can be reactivated.' },
            { label: 'Archived', color: 'bg-gray-300', desc: 'Permanently retired. Kept for historical records but cannot be reactivated.' },
          ].map((stage) => (
            <div key={stage.label} className="flex items-start gap-3 rounded-lg border border-gray-100 dark:border-gray-800 p-2.5">
              <span className={`w-3 h-3 rounded-full ${stage.color} shrink-0 mt-0.5`} />
              <div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">{stage.label}</span>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{stage.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <img
          src="/handbook/lifecycle-stages.svg"
          alt="Horizontal flow diagram showing the five playbook lifecycle stages as connected rounded pills with arrows: Draft (gray) → Active (emerald) → Superseded (amber) → Inactive (orange) → Archived (rose)"
          className="rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm mb-4 w-full"
        />
      </HSection>

      <HSection title="Publishing a Playbook">
        <HStep number={1} title="Finish building your playbook">
          Make sure all statuses, transitions, guards, validators, and actions are configured.
        </HStep>
        <HStep number={2} title="Test it first">
          Click the "Test" button in the Visual Editor toolbar to run a simulation. This verifies
          that all paths are reachable and there are no dead ends.
        </HStep>
        <HStep number={3} title="Click 'Publish'">
          The system validates the playbook structure (at least one initial status, at least one
          terminal status, no orphaned nodes). If validation passes, the playbook becomes Active.
        </HStep>
      </HSection>

      <HSection title="Versioning">
        <HParagraph>
          Every time you publish a modified playbook, the system creates a <HBold>new version</HBold>.
          The previous version is automatically marked as "Superseded." You can view all past versions
          in the Versions tab.
        </HParagraph>
        <HParagraph>
          The <HBold>Show Changes</HBold> button in the editor lets you compare any two versions
          side by side, highlighting what was added, removed, or modified.
        </HParagraph>
      </HSection>

      <HWarning>
        Publishing a new version affects all <em>new</em> matters that start after publication.
        Matters already in progress continue under the version they started with.
      </HWarning>

      <HTip>
        Always test before publishing. The simulation catches structural problems like dead-end
        statuses (statuses with no outgoing transitions) and unreachable statuses (statuses with
        no incoming transitions).
      </HTip>
    </HChapter>
  );
}
